import { get, post } from 'utils/apiWrapper';
import { prepareHeadersFromContext, handleOriginByCookie, checkStatus } from 'utils/helpers';
import { getDomainValue } from 'utils/domain';
import {
  LOGIN_ROUTE,
  USER_INFO_ROUTE,
  CREATE_ACCOUNT,
  MODIFY_ACCOUNT,
  CHANGE_PASSWORD,
  EMPLOYEE_LOGIN_ROUTE,
  RECOVER_PASSWORD,
  SET_NEW_PASSWORD,
  REGISTER_PELOTAS,
  USERS_CUSTOMER_SERVICE,
  ADD_PRODUCT_TO_WISHLIST,
  GET_WISHLIST,
  REMOVE_PRODUCT_FROM_WISHLIST,
  NEWSLETTER_CHECK_STATUS,
  DELETE_MY_ACCOUNT,
  STORE_LOGIN,
  STORE_EMPLOYEES,
  IPAD_MENU,
} from 'utils/routes';
import { getUtmParams } from 'utils/utm';
import { $fetch } from 'utils/request';
import { storage } from 'utils/web-storage';

// datos si el email ya ha hecho alguna compra
type UserLoginInfo = {
  treatment: string | null;
  username: string;
  surname: string;
  gender: string | null;
  email: string;
  address: string;
  telephone: string | null;
  city: string;
  country: string | null;
  countryId: string | null;
  language: string;
  newsletterSubscribed: boolean;
  birthDate: string | null;
  preferences: string | null;
  categories: string | null;
  primaryAddress: {
    title: string | null;
    name: string | null;
    surname: string | null;
    email: string | null;
    telephone: string | null;
    prefix: string | null;
    address: string;
    postalCode: string;
    city: string;
    region: string;
    countryId: string;
    nif: string | null;
    county: string | null;
    colonia: string | null;
    registroFederal: string | null;
    addressNumber: string | null;
  };
  alternativeAddress: string | null;
  kboix: boolean;
};
export type LoginResponse = {
  token: string;
  refreshToken: string;
  info: UserLoginInfo;
};

export async function loginV2({ email, password }: { email: string; password: string }) {
  return $fetch<LoginResponse>(
    { path: LOGIN_ROUTE },
    {
      method: 'POST',
      body: JSON.stringify({
        username: email,
        password,
      }),
    },
  );
}

export const login = (username: string, password: string) =>
  new Promise((resolve, reject) => {
    post({
      path: LOGIN_ROUTE,
      data: {
        username,
        password,
      },
    })
      .then((response) => {
        try {
          if (response && response.data) {
            const { status, token, refreshToken, info } = response.data;
            if (status === 'OK') {
              return resolve({ token, refreshToken, info });
            }
          }
          return reject(response);
        } catch (e) {
          return reject(e);
        }
      })
      .catch(reject);
  });

type RegisterResponse = {
  token: string;
  refreshToken: string;
  info?: UserLoginInfo;
};
export async function createAccountV2({
  locale,
  email,
  password,
  captchaToken,
  newsletter,
}: {
  locale: string;
  email: string;
  password: string;
  captchaToken: string;
  newsletter: boolean;
}) {
  const originFields = handleOriginByCookie();
  const utmParams = getUtmParams();
  const lab = getDomainValue({ camper: false, camperlab: true, nnormal: false });
  return await $fetch<RegisterResponse>(
    { path: CREATE_ACCOUNT },
    {
      method: 'POST',
      body: JSON.stringify({
        profile: locale,
        email,
        password,
        captchaToken,
        newsletter,
        ...originFields,
        ...utmParams,
        lab,
      }),
    },
  );
}
export const createAccount = (locale: string, accountData: Record<string, any>) =>
  new Promise((resolve, reject) => {
    const originFields = handleOriginByCookie();
    const utmParams = getUtmParams();
    const lab = getDomainValue({ camper: false, camperlab: true, nnormal: false });
    post({
      path: CREATE_ACCOUNT,
      data: {
        profile: locale,
        ...accountData,
        ...originFields,
        ...utmParams,
        lab,
      },
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          try {
            resolve(accountData);
          } catch (e) {
            reject(e);
          }
        } else {
          reject(response.data.errors[0].message);
        }
      })
      .catch(reject);
  });

export const getUserData = (locale: string, showOrders?: boolean) =>
  new Promise((resolve, reject) => {
    const token = storage.local.get('token');

    const body: Record<string, any> = {
      profile: locale,
    };
    if (typeof showOrders === 'boolean') {
      body.showOrders = showOrders;
    }
    post({
      path: `${USER_INFO_ROUTE}`,
      headers: { Authorization: `Bearer ${token}` },
      data: body,
    })
      .then((response) => {
        const { status, account, lastOrder, orders } = response.data;
        if (status === 'OK') {
          return resolve({ account, lastOrder, orders });
        }

        return reject(response);
      })
      .catch(reject);
  });

export const modifyUserData = (locale: string, data: Record<string, any>) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    post({
      path: MODIFY_ACCOUNT,
      headers: { Authorization: `Bearer ${token}` },
      data: {
        profile: locale,
        account: data,
      },
    })
      .then((response) => {
        if (response && response.data) {
          const { status, errors } = response.data;

          if (status === 'OK') {
            resolve(response);
          } else {
            reject(errors);
          }
        }
      })
      .catch(reject);
  });

export const changePassword = (oldPassword: string, newPassword: string) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    post({
      path: CHANGE_PASSWORD,
      headers: { Authorization: `Bearer ${token}` },
      data: { oldPassword, newPassword },
    }).then((response) => {
      if (response && response.data) {
        const { status, errors } = response.data;

        if (status === 'OK') {
          resolve(response.data);
        } else {
          reject(errors);
        }
      }
    });
  });

export const employeeLogin = (payload: Record<string, any>) =>
  new Promise((resolve, reject) => {
    post({
      path: EMPLOYEE_LOGIN_ROUTE,
      data: payload,
    })
      .then((response) => {
        try {
          if (response && response.data) {
            const { status, userCode, pending, max, details, currency, acumulated } = response.data;
            if (status === 'OK') {
              return resolve({
                userCode,
                pending,
                max,
                details,
                currency,
                acumulated,
              });
            }
          }
          return reject(response);
        } catch (e) {
          return reject(e);
        }
      })
      .catch(reject);
  });

export function recoverPassword(payload: { profile: string; email: string }) {
  return $fetch<{}>(
    {
      path: RECOVER_PASSWORD,
    },
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export const setNewPassword = (payload) =>
  new Promise((resolve, reject) => {
    post({
      path: SET_NEW_PASSWORD,
      data: payload,
    })
      .then((response) => {
        try {
          if (response && response.data) {
            if (response.data.status === 'OK' && response.data.message.indexOf('succes') !== -1) {
              return resolve(response.data);
            }
          }
          return reject(response);
        } catch (e) {
          return reject(e);
        }
      })
      .catch(reject);
  });

export const registerPelotas = (payload: Record<string, any>) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    const formdata = new FormData();
    Object.entries(payload).forEach((value) => {
      if (value[0] === 'ticket') {
        value[1].forEach((file) => formdata.append(value[0], file));
      } else {
        formdata.append(value[0], value[1]);
      }
    });
    post({
      path: REGISTER_PELOTAS,
      data: formdata,
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        try {
          if (response && response.data) {
            const { status, message } = response.data;
            if (status === 'OK') {
              return resolve({
                status,
                message,
              });
            }
          }
          return reject(response);
        } catch (e) {
          return reject(e);
        }
      })
      .catch(reject);
  });

export const getUsersCustomerService = ({ context }: { context: any }) =>
  new Promise((resolve, reject) => {
    const headers = prepareHeadersFromContext(context);

    get({
      path: USERS_CUSTOMER_SERVICE,
      headers,
    })
      .then((response) => {
        try {
          if (response && response.data) {
            if (response.data.status === 'OK') {
              return resolve(response.data);
            }
          }
          return reject(response);
        } catch (e) {
          return reject(e);
        }
      })
      .catch(reject);
  });

export const addProductToWishlist = (payload: Record<string, any>) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    post({
      path: ADD_PRODUCT_TO_WISHLIST,
      headers: { Authorization: `Bearer ${token}` },
      data: payload,
    }).then((response) => {
      if (response && response.data) {
        const { status, errors } = response.data;

        if (status === 'OK') {
          resolve(response.data);
        } else {
          reject(errors);
        }
      }
    });
  });

export const removeProductFromWishlist = (payload: Record<string, any>) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    post({
      path: REMOVE_PRODUCT_FROM_WISHLIST,
      headers: { Authorization: `Bearer ${token}` },
      data: payload,
    }).then((response) => {
      if (response && response.data) {
        const { status, errors } = response.data;

        if (status === 'OK') {
          resolve(response.data);
        } else {
          reject(errors);
        }
      }
    });
  });

export const getWishlist = (payload) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    post({
      path: GET_WISHLIST,
      headers: { Authorization: `Bearer ${token}` },
      data: payload,
    }).then((response) => {
      if (response && response.data) {
        const { status, errors } = response.data;

        if (status === 'OK') {
          resolve(response.data);
        } else {
          reject(errors);
        }
      }
    });
  });

export async function checkNewsletterStatus(profile: string, email: string) {
  return await $fetch<{
    status: string;
    newsletterSubscribed: boolean;
    hasPhone: boolean;
    hasName: boolean;
  }>(
    {
      path: NEWSLETTER_CHECK_STATUS,
    },
    {
      method: 'POST',
      body: JSON.stringify({
        profile,
        email,
      }),
    },
  );
}

export const deleteMyAccount = (locale: string) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    post({
      path: DELETE_MY_ACCOUNT,
      headers: { Authorization: `Bearer ${token}` },
      data: {
        profile: locale,
        token,
      },
    })
      .then((response) => {
        if (response && response.data) {
          const { status, errors, message } = response.data;

          if (status === 'OK') {
            resolve({ message });
          } else {
            reject(errors);
          }
        }
      })
      .catch(reject);
  });

export const storeLogin = (payload: Record<string, any>) =>
  new Promise((resolve, reject) => {
    post({
      path: STORE_LOGIN,
      data: payload,
    })
      .then((response) => {
        if (response && response?.data && response?.data?.status === 'OK') {
          delete response.data.status;
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const getEmployees = (profile: string, storeId: string) =>
  new Promise((resolve, reject) => {
    post({
      path: STORE_EMPLOYEES,
      data: { storeId, profile },
    })
      .then((response) => {
        if (response && response?.data && response?.data?.status === 'OK' && response?.data?.empleados) {
          resolve(response.data.empleados);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const getIpadMenu = (profile: string) =>
  new Promise<any>((resolve, reject) => {
    get({ path: `${IPAD_MENU}?profile=${profile}` })
      .then((response) => {
        if (checkStatus(response)) {
          const { ipadMenuSheet } = response.data;

          if (ipadMenuSheet) {
            resolve(ipadMenuSheet);
          }

          resolve(false);
        }
      })
      .catch(reject);
  });
